import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Droplets, Car, Sparkles, TrendingDown, Gauge } from "lucide-react";
import { Reveal } from "../components/ui-bits";

/* ---- live water-saved counter ---- */
function CountUp({ to, duration = 1800 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return <span ref={ref}>{val.toLocaleString("en-IN")}</span>;
}

export function WaterSavedCounter() {
  return (
    <section className="light-section relative py-16 lg:py-20" data-testid="eco-counter">
      <div className="dot-grid-light pointer-events-none absolute inset-0" />
      <div className="container-x relative">
        <div className="mx-auto max-w-xl text-center">
          <Reveal><span className="eyebrow eyebrow-dark">Our Growing Impact</span></Reveal>
          <Reveal delay={0.06}>
            <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-nighttext sm:text-4xl">
              Clean Cars. <span className="text-carza">Fuller Reservoirs.</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="mx-auto mt-9 flex max-w-lg items-center gap-3 rounded-3xl border border-black/[0.06] bg-white p-5 shadow-[0_30px_70px_-35px_rgba(23,19,31,0.35)] sm:gap-5 sm:p-7" data-testid="water-counter-card">
            <span className="shrink-0 text-carza">
              <svg width="42" height="42" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 2.5c2.6 3.2 6.5 8.2 6.5 12a6.5 6.5 0 1 1-13 0c0-3.8 3.9-8.8 6.5-12z"/></svg>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] leading-snug text-[#5c5470]">Water saved this month:</p>
              <p className="font-display text-2xl font-bold tracking-tight text-nighttext sm:text-[28px]" data-testid="water-counter">
                <CountUp to={76800} /> <span className="text-lg sm:text-xl">Litres</span>
              </p>
            </div>
            <span className="h-14 w-px shrink-0 bg-black/[0.08]" />
            <div className="flex shrink-0 items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-carza-dim text-carza"><Gauge size={24} /></span>
              <div>
                <p className="font-display text-2xl font-bold leading-none text-nighttext sm:text-3xl"><CountUp to={640} /></p>
                <p className="mt-1 text-[10.5px] font-medium text-[#8a8399]">washes</p>
              </div>
            </div>
          </div>
          <p className="mt-4 text-center text-[13px] text-[#5c5470]">across <span className="font-semibold text-nighttext">1,420+ washes</span> in Delhi NCR — and counting.</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
            {["80% less water per wash", "~30 min wash time", "No detergent runoff"].map((chip) => (
              <span key={chip} className="flex items-center gap-1.5 rounded-full border border-carza/25 bg-carza-dim/60 px-4 py-1.5 text-[12px] font-semibold text-carza">
                <Droplets size={12} /> {chip}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---- animated 150L vs 30L bars ---- */
export function WaterComparisonBars() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.35 });
  return (
    <section className="light-section relative py-16 lg:py-20">
      <div className="dot-grid-light pointer-events-none absolute inset-0" />
      <div className="container-x relative">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal><span className="eyebrow eyebrow-dark">The Water Math</span></Reveal>
          <Reveal delay={0.06}>
            <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-nighttext sm:text-4xl">
              One Wash. Two Very Different <span className="text-carza">Water Stories.</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div ref={ref} className="mx-auto mt-10 max-w-3xl rounded-[2rem] border border-carza/15 bg-white p-6 shadow-[0_30px_70px_-40px_rgba(23,19,31,0.35)] sm:p-10" data-testid="water-bars">
            {/* traditional */}
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-black/[0.05] text-[#8a8399]"><Car size={18} /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-[13px] font-bold text-nighttext">Traditional bucket &amp; hose wash</p>
                  <p className="shrink-0 font-mono text-sm font-bold text-[#8a8399]">150 L</p>
                </div>
                <div className="mt-2 h-6 overflow-hidden rounded-full bg-black/[0.05]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={inView ? { width: "100%" } : {}}
                    transition={{ duration: 1.4, ease: "easeOut", delay: 0.2 }}
                    className="h-full rounded-full bg-gradient-to-r from-[#c9c4d6] to-[#8a8399]"
                  />
                </div>
              </div>
            </div>
            {/* carza */}
            <div className="mt-6 flex items-center gap-3 sm:gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-carza-dim text-carza"><Droplets size={18} /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-[13px] font-bold text-nighttext">CARZA controlled-solution wash</p>
                  <p className="shrink-0 font-mono text-sm font-bold text-carza">30 L</p>
                </div>
                <div className="mt-2 h-6 overflow-hidden rounded-full bg-black/[0.05]">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={inView ? { width: "20%" } : {}}
                    transition={{ duration: 1.2, ease: "easeOut", delay: 1.1 }}
                    className="h-full rounded-full bg-gradient-to-r from-carza to-carza-lite"
                  />
                </div>
              </div>
            </div>
            <div className="mt-7 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-lavender-2/70 px-5 py-4">
              <p className="flex items-center gap-2 text-[13px] font-semibold text-nighttext">
                <TrendingDown size={15} className="text-carza" /> 80% less water — every single wash
              </p>
              <span className="rounded-full bg-carza px-4 py-1.5 text-[11px] font-bold text-white">120 L saved / wash</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---- two glasses visual ---- */
function Glass({ fill, label, value, accent }) {
  // fill: 0..1 — water height animates from 0
  const H = 150, W = 84;
  const waterH = (H - 16) * fill;
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative overflow-hidden rounded-b-2xl rounded-t-md border-[3px] border-nighttext/80 bg-white/70" style={{ width: W, height: H }}>
        <motion.div
          initial={{ height: 0 }}
          whileInView={{ height: waterH }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.3, ease: "easeOut", delay: accent ? 0.7 : 0.15 }}
          className="absolute inset-x-0 bottom-0"
          style={{ background: accent ? "linear-gradient(180deg, rgba(109,61,245,0.9), rgba(124,77,255,0.95))" : "linear-gradient(180deg, rgba(138,131,153,0.5), rgba(138,131,153,0.7))" }}
        >
          <span className="absolute inset-x-0 top-0 h-1.5 bg-white/40" />
        </motion.div>
        <span className="absolute inset-x-0 top-2 text-center font-mono text-[10px] font-bold text-nighttext/70">{value}</span>
      </div>
      <p className="max-w-[140px] text-center text-[12px] font-semibold leading-snug text-nighttext">{label}</p>
    </div>
  );
}

export function TwoGlasses() {
  return (
    <section className="container-x pb-6">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#241543] via-carza-deep to-[#1b0f33] p-7 sm:p-10" data-testid="two-glasses">
          <div className="dot-grid pointer-events-none absolute inset-0 opacity-60" />
          <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="eyebrow">Same Car. Same Shine.</span>
              <h2 className="mt-4 font-display text-2xl font-bold leading-tight text-paper sm:text-3xl">The Only Difference Is the Water.</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-mist">
                CARZA's controlled-solution process cleans with measured micro-doses instead of free-flowing hoses — engineered for metro cities where every litre counts.
              </p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <span className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-3.5 py-1.5 text-[11.5px] font-semibold text-paper"><Sparkles size={12} className="text-[#9BE2FF]" /> Streak-free finish</span>
                <span className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-3.5 py-1.5 text-[11.5px] font-semibold text-paper"><Droplets size={12} className="text-[#9BE2FF]" /> ~30 min doorstep wash</span>
              </div>
            </div>
            <div className="flex items-end justify-center gap-10 sm:gap-14">
              <Glass fill={1} label="Traditional wash — 150 L" value="150 L" />
              <Glass fill={0.2} label="CARZA wash — 30 L" value="30 L" accent />
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}